/**
 * Sentry-integrated Error Boundaries for different application sections
 * Provides specialized error handling for different components and features
 */

import React from 'react';
// Lazy bridge: avoids pulling Sentry into initial chunk
import { setSentryErrorBoundaryComponentLazy } from '@/lib/sentry-lazy';
import { ErrorBoundary, type ErrorBoundaryCopy } from './ErrorBoundary';
import { sentryLogger, captureError } from '@/lib/sentry-config';

// Sentry Error Boundary wrapper with additional context
interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  name: string;
  feature?: string;
  level?: 'page' | 'component' | 'critical';
  beforeCapture?: (scope: any, error: Error, errorInfo: React.ErrorInfo) => void;
  copy?: ErrorBoundaryCopy;
}

export const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({
  children,
  fallback,
  name,
  feature = 'unknown',
  level = 'component',
  beforeCapture,
  copy,
}) => {
  return (
    // Defer to our internal ErrorBoundary; set Sentry tag lazily for context
    // We still use the local ErrorBoundary UI, ensuring no hard dependency on Sentry
    <ErrorBoundary
      level={level}
      copy={copy}
      onError={(error: Error, _errorInfo: React.ErrorInfo) => {
        setSentryErrorBoundaryComponentLazy(`ErrorBoundary-${name}`);
        sentryLogger.error(`Error in ${name}`, {
          component: name,
          feature,
          level,
          error: (error as Error).message,
        });
        if (beforeCapture) {
          try {
            // Provide minimal compatible args
            beforeCapture({} as any, error as Error, { componentStack: '' } as React.ErrorInfo);
          } catch (e) {
            // swallow errors from beforeCapture safely
          }
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

// Pre-configured error boundaries for different application sections

export const DashboardErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SentryErrorBoundary
    name="Dashboard"
    feature="dashboard"
    level="page"
    beforeCapture={(scope, error) => {
      scope.setTag('user_flow', 'dashboard_navigation');
      sentryLogger.warn('Dashboard error occurred', { error: error.message });
    }}
  >
    {children}
  </SentryErrorBoundary>
);

/** Calm, student-facing wording for a session page that fails to load. */
export const SESSION_ERROR_COPY: ErrorBoundaryCopy = {
  title: 'Something went wrong loading this session',
  retryLabel: 'Retry',
  homeAction: { label: 'Back to dashboard', href: '/dashboard' },
};

export const SessionErrorBoundary: React.FC<{ children: React.ReactNode; sessionId?: string }> = ({ 
  children, 
  sessionId 
}) => (
  <SentryErrorBoundary
    name="CoachingSession"
    feature="coaching_session"
    level="critical"
    copy={SESSION_ERROR_COPY}
    beforeCapture={(scope, error) => {
      scope.setTag('user_flow', 'coaching_session');
      if (sessionId) {
        scope.setTag('session_id', sessionId);
        scope.setContext('session_info', { sessionId });
      }
      sentryLogger.error('Critical session error', { 
        sessionId, 
        error: error.message,
        severity: 'high',
      });
    }}
  >
    {children}
  </SentryErrorBoundary>
);

export const AuthErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SentryErrorBoundary
    name="Authentication"
    feature="auth"
    level="critical"
    beforeCapture={(scope, error) => {
      scope.setTag('user_flow', 'authentication');
      scope.setTag('auth_error', 'true');
      sentryLogger.error('Authentication error', { 
        error: error.message,
        severity: 'high',
      });
    }}
  >
    {children}
  </SentryErrorBoundary>
);

export const PaymentErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SentryErrorBoundary
    name="Payment"
    feature="payments"
    level="critical"
    beforeCapture={(scope, error) => {
      scope.setTag('user_flow', 'payment');
      scope.setTag('payment_error', 'true');
      sentryLogger.fatal('Payment system error', { 
        error: error.message,
        severity: 'critical',
      });
    }}
  >
    {children}
  </SentryErrorBoundary>
);

export const ComponentErrorBoundary: React.FC<{ 
  children: React.ReactNode;
  componentName: string;
  feature?: string;
}> = ({ children, componentName, feature = 'ui' }) => (
  <SentryErrorBoundary
    name={componentName}
    feature={feature}
    level="component"
    beforeCapture={(scope, error) => {
      scope.setTag('component_error', 'true');
      sentryLogger.debug(`Component error in ${componentName}`, { 
        component: componentName,
        error: error.message,
      });
    }}
  >
    {children}
  </SentryErrorBoundary>
);

// HOC for wrapping components with error boundaries
export const withSentryErrorBoundary = <P extends Record<string, unknown>>(
  Component: React.ComponentType<P>,
  options: {
    name: string;
    feature?: string;
    level?: 'page' | 'component' | 'critical';
  }
) => {
  const WrappedComponent = (props: P) => (
    <SentryErrorBoundary {...options}>
      <Component {...props} />
    </SentryErrorBoundary>
  );
  
  WrappedComponent.displayName = `withSentryErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for manual error reporting within components
export const useSentryErrorHandler = (componentName: string, feature: string = 'unknown') => {
  const reportError = React.useCallback((error: Error, context?: Record<string, unknown>) => {
    captureError(error, {
      component: componentName,
      feature,
      extra: context,
    });
    
    sentryLogger.error(`Manual error report from ${componentName}`, {
      component: componentName,
      feature,
      error: error.message,
      context,
    });
  }, [componentName, feature]);
  
  const reportMessage = React.useCallback((
    message: string, 
    level: 'info' | 'warning' | 'error' = 'info',
    context?: Record<string, unknown>
  ) => {
    const mapped = level === 'warning' ? 'warn' : level;
    (sentryLogger as any)[mapped]?.(`${componentName}: ${message}`, {
      component: componentName,
      feature,
      ...context,
    });
  }, [componentName, feature]);
  
  return { reportError, reportMessage };
};
